import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Coffee, Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { CardSkeleton } from '@/components/ui/card-skeleton';
import {
  PageHead,
  PageToolbar,
  PageWithAside,
  PendingDecision,
  SectionTitle,
} from '@/components/editorial';
import { Button } from '@/components/ui/button';
import { ConfirmActionDialog } from '@/components/ui/ConfirmActionDialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanionName } from '@/hooks/useCompanionName';
import { useOnboardingGuide } from '@/contexts/OnboardingGuideContext';
import { TaskRow } from '@/components/tasks/TaskRow';
import { TaskFilterPanel } from '@/components/tasks/TaskFilterPanel';
import { StevensCallout, buildSuggestions } from '@/components/tasks/StevensCallout';
import { RequestHelpModal } from '@/components/tasks/RequestHelpModal';
import { ErrorStateCard } from '@/components/ui/ErrorStateCard';
import { FirstRunCard } from '@/components/onboarding/FirstRunCard';
import { isNoFamilyError } from '@/lib/api-errors';
import { localizedApiErrorMessage } from '@/lib/utils';
import { NAV_KEY } from '@/i18n/keys/nav';
import { navigateToStevens } from '@/lib/chat-navigation';
import {
  DEFAULT_TASK_FILTERS,
  formatRecurrenceLabel,
  groupSeriesByWhen,
  groupTasksBySeries,
  isTaskFiltered,
  matchesUiStatus,
  matchesUiUrgency,
  taskMatchesSearch,
  visibleParentTasks,
  type TaskFilterState,
  type TaskSeries,
  type WhenGroup,
  WHEN_LABEL_KEY,
} from '@/lib/task-display';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import { labelFor } from '@/i18n/labels';
import { useMsg } from '@/i18n/msg';
import { api } from '@/services/api';
import type { Task } from '@/types/api';

interface TaskCreateDefaults {
  date?: Date;
  category?: 'meal' | 'ride' | 'childcare' | 'errand' | 'hospital_stay' | 'other';
  title?: string;
}

/**
 * /tasks after the 2026-09-10 declutter (spec §4, "say each thing once") and
 * the same day's second pass: no headline — the eyebrow is the H1; one icon
 * toolbar carries add · search · filter; the status pills and chip rows live
 * in the filter popover; the companion is a right-column card only when she
 * has something to say. The list is a task-manager list, not a card grid:
 * one line per task, grouped by WHEN it is due (Overdue · Today · Tomorrow ·
 * Next seven days · Later · No date), with finished tasks in a Done group at
 * the end that opens on request. Every parent-created row carries a pencil
 * that opens the request modal in edit mode; the trash asks first.
 */
/**
 * The last non-null value. A confirm dialog opens on `subject !== null` but
 * keeps painting through its close animation; reading the subject straight
 * from state would flash an empty title ("Cancel “”?") in that frame. The
 * remembered value is set during render (React's "adjust state on a prop
 * change" pattern), so the first open already has it.
 */
function useLastNonNull<T>(value: T | null): T | null {
  const [last, setLast] = useState<T | null>(value);
  if (value !== null && value !== last) setLast(value);
  return value ?? last;
}

export function TasksPage() {
  const { t, i18n } = useTranslation(['tasks', 'nav']);
  const resolve = useMsg();
  const language = i18n.resolvedLanguage ?? i18n.language;
  // R3-B11: the toast description is the server's English only for an
  // English reader; `i18n.language` is the reader's choice.
  const errorText = (e: unknown) => localizedApiErrorMessage(e, t, i18n.language) || t('page.toast.pleaseTryAgain');
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const companionName = useCompanionName();
  const { guideStep, completeGuide } = useOnboardingGuide();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDefaults, setCreateDefaults] = useState<TaskCreateDefaults>({});
  const [filters, setFilters] = useState<TaskFilterState>(DEFAULT_TASK_FILTERS);
  const [search, setSearch] = useState('');
  // The two modals are separate elements with separate open state — the
  // create instance keeps its defaults, the edit instance keeps its task.
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  // Cancel has no undo and no restore, so it asks first (C6, D-C).
  const [pendingCancel, setPendingCancel] = useState<Task | null>(null);
  // The series confirm keeps the representative task, not only its
  // recurrence id, so its heading can name the task (#2413).
  const [pendingCancelSeries, setPendingCancelSeries] = useState<Task | null>(null);
  // The dialogs read these, so their titles survive the close animation.
  const shownCancel = useLastNonNull(pendingCancel);
  const shownCancelSeries = useLastNonNull(pendingCancelSeries);

  // #1083 (B-065): a push deep-link lands on /tasks/:taskId. Once the list has
  // rendered, scroll the card into view, move focus to it, and ring it briefly.
  // If the id is not in the current view, say so instead of failing silently.
  const { taskId: deepLinkTaskId } = useParams<{ taskId: string }>();
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  const [deepLinkMissing, setDeepLinkMissing] = useState(false);
  const handledDeepLinkRef = useRef<string | null>(null);

  const isParent = user?.role === 'parent';

  const openCreateDialog = (defaults: TaskCreateDefaults = {}) => {
    setCreateDefaults(defaults);
    setShowCreateDialog(true);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      openCreateDialog();
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const {
    data: tasksResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.tasks.getAll(),
    refetchInterval: 30000,
  });

  // #2411: under the shared ['medical', 'appointments'] prefix, so the
  // calendar's create, edit, status and delete invalidations reach the
  // companion card here instead of leaving it on a 5-minute-old list.
  const { data: appointmentsData } = useQuery({
    queryKey: ['medical', 'appointments', 'upcoming'],
    queryFn: () => api.medical.appointments.getAll(true),
  });

  // #693: the memorial gate, reached through the shared accessor rather than
  // re-derived here. `modeLoading` holds the list back for the one render
  // where the family record has not landed — the fail-safe default is
  // 'active', so without it a bereaved parent would see their child's
  // treatment tasks flash in and then vanish, which is worse than a blank
  // beat.
  const { mode, isLoading: modeLoading } = useDashboardMode();

  const tasks: Task[] = tasksResponse?.data || [];
  // The appointments feed the Stevens callout, not the task list — and the
  // callout says "You haven't asked for help with rides this week. <Chemo
  // infusion> is tomorrow at 9:00 AM" behind a "Draft a ride request" button.
  // Gating only `tasks` left that sentence on the page for a bereaved family,
  // beside a list this page had correctly emptied. Treatment records are
  // withheld whole — same shape as CalendarPage.tsx:146 and DashboardPage.
  const appointments = useMemo(
    () => (modeLoading || mode === 'memorial' ? [] : appointmentsData?.data || []),
    [appointmentsData, mode, modeLoading],
  );

  const visibleTasks = useMemo(
    () =>
      modeLoading
        ? []
        : visibleParentTasks(tasks, mode).filter((t) => t.status !== 'cancelled'),
    [tasks, mode, modeLoading],
  );

  const filteredTasks = useMemo(() => {
    return visibleTasks.filter((task) => {
      if (!matchesUiStatus(task, filters.status)) return false;
      if (filters.category && task.category !== filters.category) return false;
      if (!matchesUiUrgency(task, filters.urgency)) return false;
      if (!taskMatchesSearch(task, search, t)) return false;
      return true;
    });
  // `language` rebuilds the match on a switch: the category label searched is the reader's.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTasks, filters, search, language]);

  // The Done group is a logbook: closed unless asked for, unless the filter
  // is already asking for finished tasks only, or unless a search is open —
  // a search that could not reach a finished task would read as "not found".
  const [doneOpen, setDoneOpen] = useState(false);
  const showDone = doneOpen || filters.status === 'completed' || search.trim().length > 0;

  const groups: WhenGroup[] = useMemo(() => {
    // When showEveryOccurrence is on, treat each task as its own singleton
    // series so the render loop stays uniform (just no expand affordance).
    const seriesList: TaskSeries[] = filters.showEveryOccurrence
      ? filteredTasks.map((t) => ({ key: t._id, representative: t, occurrences: [t], isSeries: false }))
      : groupTasksBySeries(filteredTasks);
    return groupSeriesByWhen(seriesList);
  }, [filteredTasks, filters.showEveryOccurrence]);

  // #2382 step 7: React Query keeps the last data when a background refetch
  // fails (a 429 from the 30 s poll). Only a load that never produced a list
  // is an error; a failed refetch keeps the rows the parent was reading.
  const loadFailed = !!error && !tasksResponse;
  const listReady = !isLoading && !modeLoading && !loadFailed;

  useEffect(() => {
    if (!deepLinkTaskId || !listReady) return;
    // Handle each id once: the 30 s refetch and filter changes must not
    // re-scroll a list the user has already moved through.
    if (handledDeepLinkRef.current === deepLinkTaskId) return;
    handledDeepLinkRef.current = deepLinkTaskId;

    // A sibling of a collapsed series has no row of its own — the series
    // row (its representative) is the landing spot.
    let targetId: string | null = null;
    for (const g of groups) {
      const hit = g.items.find((s) => s.occurrences.some((o) => o._id === deepLinkTaskId));
      if (hit) {
        targetId = hit.representative._id;
        // A finished task lives in the closed Done group; open it so the
        // row exists to scroll to.
        if (g.key === 'done') setDoneOpen(true);
        break;
      }
    }
    if (!targetId) {
      setDeepLinkMissing(true);
      return;
    }

    // Deferred one tick so ScrollToTop's route-change effect (which resets
    // the scroll container) has already run and cannot undo this scroll.
    const el = document.getElementById(`task-${targetId}`);
    const scrollTimer = window.setTimeout(() => {
      el?.scrollIntoView({ block: 'center' });
      el?.focus({ preventScroll: true });
    }, 0);
    setHighlightedTaskId(targetId);
    // Deliberately not cancelled on cleanup: `groups` can change within the
    // 2 s window (a refetch landing), and cancelling would leave the ring on.
    window.setTimeout(() => setHighlightedTaskId(null), 2000);
    return () => window.clearTimeout(scrollTimer);
  }, [deepLinkTaskId, listReady, groups]);

  const completeMutation = useMutation({
    mutationFn: (taskId: string) => api.tasks.complete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      // #2414: the title alone; the old body thanked the parent "for helping" with her own task.
      toast({ title: t('page.toast.completed') });
    },
    onError: (e: Error) => {
      toast({ variant: 'destructive', title: t('page.toast.completeFailed'), description: errorText(e) });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (taskId: string) => api.tasks.cancel(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: t('page.toast.canceled'), description: t('page.toast.canceledBody') });
    },
    onError: (e: Error) => {
      toast({ variant: 'destructive', title: t('page.toast.cancelFailed'), description: errorText(e) });
    },
  });

  const cancelSeriesMutation = useMutation({
    mutationFn: (recurrenceId: string) => api.tasks.cancelRecurrence(recurrenceId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      const count = res?.data?.cancelled_count ?? 0;
      toast({
        title: t('page.toast.seriesCanceled'),
        description: count > 0
          ? t('page.toast.seriesCanceledCount', { count })
          : t('page.toast.seriesCanceledBody'),
      });
    },
    onError: (e: Error) => {
      toast({ variant: 'destructive', title: t('page.toast.seriesCancelFailed'), description: errorText(e) });
    },
  });

  const handleTaskCreated = async () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    if (guideStep === 'request-btn') {
      await completeGuide();
      toast({ title: t('page.toast.firstTask'), description: t('page.toast.firstTaskBody') });
      navigate('/dashboard');
    }
  };

  // Every cancel — a card, a series sibling in the expanded list — resolves
  // to the task and waits on the confirm. The id is what TaskCard hands back.
  const askToCancel = (fallback: Task) => (id: string) =>
    setPendingCancel(tasks.find((t) => t._id === id) ?? fallback);

  const clearNarrowing = () => {
    setFilters(DEFAULT_TASK_FILTERS);
    setSearch('');
  };

  const stevensSuggestions = useMemo(
    () =>
      buildSuggestions({
        tasks: visibleTasks,
        appointments,
        onDraftRideRequest: (defaultDate, suggestedTitle) =>
          openCreateDialog({ date: defaultDate, category: 'ride', title: resolve(suggestedTitle) }),
        // R3-B2: the prompt is sent in the UI language.
        onTalkToStevens: (prompt) => navigateToStevens(navigate, resolve(prompt)),
        onFilterUrgent: () => setFilters((f) => ({ ...f, status: 'open', urgency: 'urgent' })),
        companionName,
        locale: language,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleTasks, appointments, companionName, language],
  );

  const isNarrowed = isTaskFiltered(filters) || search.trim().length > 0;
  // #2415: every task is finished. The only group is the closed Done
  // logbook, so without this the page read as a lone "Done · Show" line.
  // A narrowed view that happens to reach only finished tasks is not this.
  const caughtUp = !isNarrowed && groups.length > 0 && groups.every((g) => g.key === 'done');
  const ownerId = user?.id || user?._id;

  return (
    <PageWithAside
      aside={isParent && stevensSuggestions.length > 0
        ? <StevensCallout suggestions={stevensSuggestions} layout="card" />
        : null}
    >
      <div className="space-y-6">
        <header className="space-y-4">
          <PageHead eyebrowKey={NAV_KEY.tasks} />
          <PageToolbar
            label={t(NAV_KEY.tasks)}
            add={isParent ? {
              items: [{ label: t('page.empty.addTask'), onSelect: () => openCreateDialog() }],
              // The onboarding guide and the companion spotlight read these
              // (OnboardingGuideChip, stevens-navigation `add-task`).
              buttonProps: { 'data-guide': 'request-btn', 'data-spotlight': 'add-task' },
              className: guideStep === 'request-btn' ? 'ring-2 ring-offset-2 ring-stevens-indigo animate-pulse' : undefined,
            } : undefined}
            search={{ value: search, onChange: setSearch, placeholder: t('page.searchPlaceholder') }}
            filter={{
              active: isTaskFiltered(filters),
              label: t('page.filterLabel'),
              panel: (
                <>
                  <TaskFilterPanel filters={filters} onChange={setFilters} />
                  {/* A pending-decision caption for TRF, not family copy: it stays English (R3-B20). */}
                  <p className="flex items-center gap-1.5 text-caption text-stevens-ink-3" lang="en">
                    Status is a filter, not a section
                    <PendingDecision id="tasks-status-shape" />
                  </p>
                </>
              ),
            }}
          />
        </header>

        {deepLinkMissing && (
          <p
            role="status"
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-muted/40 px-4 py-2.5 text-body text-foreground"
          >
            <span>{t('page.deepLinkMissing')}</span>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => {
                clearNarrowing();
                setDeepLinkMissing(false);
              }}
            >
              {t('page.showAll')}
            </Button>
          </p>
        )}

        {isLoading || modeLoading ? (
          <CardSkeleton count={4} />
        ) : loadFailed && isNoFamilyError(error) ? (
          <FirstRunCard variant="tasks" />
        ) : loadFailed ? (
          <ErrorStateCard message={t('page.loadError')} />
        ) : filteredTasks.length === 0 ? (
          <EmptyTasks
            variant={isNarrowed ? 'filtered' : 'empty'}
            onCreate={() => openCreateDialog()}
            onClear={clearNarrowing}
          />
        ) : (
          <div className="space-y-6" id="task-list">
            {caughtUp && <EmptyTasks variant="allDone" onCreate={() => openCreateDialog()} onClear={clearNarrowing} />}
            {groups.map((g, gi) => {
              const isDone = g.key === 'done';
              return (
                <section key={g.key} aria-labelledby={`task-group-${g.key}`}>
                  <div className="mb-1 flex items-baseline gap-2 border-b border-border/60 pb-1">
                    <SectionTitle as="h2" level="card-title" id={`task-group-${g.key}`}>
                      {labelFor(WHEN_LABEL_KEY, g.key, t)}
                    </SectionTitle>
                    {/* The layout's (?) sits beside the first heading — the
                        element the decision is about — not inside it, so the
                        heading's name stays the word. */}
                    {gi === 0 && <PendingDecision id="tasks-grouped-by-when" side="right" />}
                    {isDone && (
                      <button
                        type="button"
                        onClick={() => setDoneOpen((v) => !v)}
                        aria-expanded={showDone}
                        aria-controls="task-group-done-list"
                        className="ml-auto text-label text-stevens-ink-3 hover:text-foreground"
                      >
                        {showDone ? t('page.hideDone') : t('page.showDone')}
                      </button>
                    )}
                  </div>
                  {(!isDone || showDone) && (
                    <ul id={isDone ? 'task-group-done-list' : undefined} className="divide-y divide-border/60">
                      {g.items.map((series) => {
                        const task = series.representative;
                        const isOwner = task.created_by === ownerId;
                        const siblings = series.isSeries
                          ? series.occurrences.filter((o) => o._id !== task._id)
                          : [];
                        const seriesProp = series.isSeries
                          ? {
                              label: resolve(formatRecurrenceLabel(task.recurrence)),
                              siblings,
                              onCancelAll: task.recurrence_id && isOwner
                                ? () => setPendingCancelSeries(task)
                                : undefined,
                            }
                          : undefined;
                        return (
                          <TaskRow
                            key={series.key}
                            task={task}
                            when={g.key}
                            onEdit={setEditingTask}
                            onComplete={(id) => completeMutation.mutate(id)}
                            onCancel={askToCancel(task)}
                            isOwner={isOwner}
                            series={seriesProp}
                            highlighted={highlightedTaskId === task._id}
                          />
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}

        <RequestHelpModal
          open={showCreateDialog}
          onOpenChange={(o) => { setShowCreateDialog(o); if (!o) setCreateDefaults({}); }}
          onCreated={handleTaskCreated}
          defaultCategory={createDefaults.category}
          defaultTitle={createDefaults.title}
          defaultDate={createDefaults.date}
        />
        <RequestHelpModal
          open={editingTask !== null}
          editing={editingTask}
          onOpenChange={(o) => { if (!o) setEditingTask(null); }}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['tasks'] })}
          onCreated={() => {}}
        />
        <ConfirmActionDialog
          open={pendingCancel !== null}
          onOpenChange={(o) => { if (!o) setPendingCancel(null); }}
          title={t('page.confirmCancel.title', { title: shownCancel?.title ?? '' })}
          description={t('page.confirmCancel.description')}
          confirmLabel={t('page.confirmCancel.confirm')}
          cancelLabel={t('page.confirmCancel.keep')}
          onConfirm={() => {
            if (pendingCancel) cancelMutation.mutate(pendingCancel._id);
            setPendingCancel(null);
          }}
        />
        <ConfirmActionDialog
          open={pendingCancelSeries !== null}
          onOpenChange={(o) => { if (!o) setPendingCancelSeries(null); }}
          title={t('page.confirmCancelSeries.title', { title: shownCancelSeries?.title ?? '' })}
          description={t('page.confirmCancelSeries.description')}
          confirmLabel={t('page.confirmCancelSeries.confirm')}
          cancelLabel={t('page.confirmCancelSeries.keep')}
          onConfirm={() => {
            if (pendingCancelSeries?.recurrence_id) cancelSeriesMutation.mutate(pendingCancelSeries.recurrence_id);
            setPendingCancelSeries(null);
          }}
        />
      </div>
    </PageWithAside>
  );
}

interface EmptyTasksProps {
  /** `empty`: no tasks at all. `filtered`: a search or filter matched nothing. `allDone`: every task is finished (#2415). */
  variant: 'empty' | 'filtered' | 'allDone';
  onCreate: () => void;
  onClear: () => void;
}

function EmptyTasks({ variant, onCreate, onClear }: EmptyTasksProps) {
  const { t } = useTranslation('tasks');
  const hasFilters = variant === 'filtered';
  // Literal keys, so the extractor sees each one.
  const copy = {
    empty: { title: t('page.empty.title'), body: t('page.empty.body') },
    filtered: { title: t('page.empty.filteredTitle'), body: t('page.empty.filteredBody') },
    allDone: { title: t('page.empty.allDoneTitle'), body: t('page.empty.allDoneBody') },
  }[variant];
  return (
    <Card className={variant === 'allDone' ? 'px-6 py-8 text-center shadow-none' : 'px-6 py-12 text-center shadow-none'}>
      <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Coffee className="h-5 w-5 text-stevens-ink-3" />
      </div>
      <p className="text-card-title font-medium text-foreground mb-1.5">
        {copy.title}
      </p>
      <p className="text-body text-stevens-ink-3 max-w-sm mx-auto mb-5">
        {copy.body}
      </p>
      {hasFilters ? (
        <Button onClick={onClear} size="sm" variant="outline">
          {t('page.empty.showAllTasks')}
        </Button>
      ) : (
        <Button onClick={onCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {/* The same verb as the copy above it (#2532). */}
          {t('page.empty.addTask')}
        </Button>
      )}
    </Card>
  );
}
